/**
 * DrillHoleRenderer — R3F component rendering drill holes as cylinders.
 *
 * Each drill point is transformed by its parent placement's position/rotation/mirror,
 * then rendered as a colored cylinder (blue for finite depth, red for "through").
 */

import { useMemo } from 'react';
import * as THREE from 'three';
import type { HardwarePlacement } from '@/lib/hardware-placement/types';
import type { DrillPointConfig } from '@/lib/hardware-placement/types';

interface DrillHoleRendererProps {
  drillPoints: Record<string, DrillPointConfig>;
  placements: HardwarePlacement[];
  visible: boolean;
}

interface DrillHoleData {
  key: string;
  position: [number, number, number];
  radius: number;
  height: number;
  color: string;
}

export default function DrillHoleRenderer({ drillPoints, placements, visible }: DrillHoleRendererProps) {
  const holes = useMemo(() => {
    const result: DrillHoleData[] = [];

    for (let pi = 0; pi < placements.length; pi++) {
      const placement = placements[pi];

      // Build a rotation matrix from the placement rotation
      const euler = new THREE.Euler(
        placement.rotation.x,
        placement.rotation.y,
        placement.rotation.z,
      );
      const rotMatrix = new THREE.Matrix4().makeRotationFromEuler(euler);

      const drillEntries = Object.entries(drillPoints);
      for (let di = 0; di < drillEntries.length; di++) {
        const [pointName, dp] = drillEntries[di];

        // Start with the drill point position
        let dpX = dp.x;
        const dpY = dp.y;
        const dpZ = dp.z;

        // If placement is mirrored, negate X of the drill point position
        if (placement.mirrored) {
          dpX = -dpX;
        }

        // Transform drill point position by placement rotation
        const vec = new THREE.Vector3(dpX, dpY, dpZ);
        vec.applyMatrix4(rotMatrix);

        // Add placement position
        const worldX = vec.x + placement.position.x;
        const worldY = vec.y + placement.position.y;
        const worldZ = vec.z + placement.position.z;

        const isThrough = dp.depth === 'through';
        const height = isThrough ? 1 : (dp.depth as number);
        const radius = dp.diameter / 2;
        const color = isThrough ? '#ff4444' : '#4488ff';

        result.push({
          key: `drill-${pi}-${di}-${pointName}`,
          position: [worldX, worldY, worldZ],
          radius,
          height,
          color,
        });
      }
    }

    return result;
  }, [drillPoints, placements]);

  return (
    <group visible={visible}>
      {holes.map((hole) => (
        <mesh key={hole.key} position={hole.position}>
          <cylinderGeometry args={[hole.radius, hole.radius, hole.height, 12]} />
          <meshStandardMaterial
            color={hole.color}
            emissive={hole.color}
            emissiveIntensity={0.4}
          />
        </mesh>
      ))}
    </group>
  );
}
